import { useMemo, useState } from "react";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import PageHeader from "@/components/base/PageHeader";
import EmptyState from "@/components/base/EmptyState";
import Pagination from "@/components/base/Pagination";
import ArticleCard from "@/components/feature/ArticleCard";
import ArticleFilterPanel, { type ArticleSortKey } from "./components/ArticleFilterPanel";
import { articles } from "@/mocks/articles";
import usePageMeta from "@/hooks/usePageMeta";

const PAGE_SIZE = 6;
const topics = ["全部主题", ...Array.from(new Set(articles.map((item) => item.tag)))];
const allTags = Array.from(new Set(articles.flatMap((item) => item.tags)));

export default function ArticlesPage() {
  usePageMeta(
    "趣味文章 - 挠脚心网站",
    "阅读挠脚心网站的趣味文章，涵盖挠痒科普、体验笔记与轻松生活提案，用舒服的节奏了解这个有趣的话题。",
    "趣味文章,挠痒科普,挠脚心文章,体验笔记,生活方式"
  );

  const [topic, setTopic] = useState("全部主题");
  const [sort, setSort] = useState<ArticleSortKey>("latest");
  const [activeTags, setActiveTags] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const reset = () => {
    setTopic("全部主题");
    setSort("latest");
    setActiveTags([]);
    setPage(1);
  };

  const filtered = useMemo(() => {
    const list = articles.filter((item) => {
      if (topic !== "全部主题" && item.tag !== topic) return false;
      if (activeTags.length > 0 && !activeTags.some((tag) => item.tags.includes(tag))) return false;
      return true;
    });

    return list.sort((a, b) => {
      if (sort === "hot") return b.heat - a.heat;
      if (sort === "oldest") return a.date < b.date ? -1 : 1;
      return a.date < b.date ? 1 : -1;
    });
  }, [topic, sort, activeTags]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const currentPage = Math.min(page, Math.max(1, totalPages));
  const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleTagToggle = (tag: string) => {
    setPage(1);
    setActiveTags((prev) =>
      prev.includes(tag) ? prev.filter((item) => item !== tag) : [...prev, tag]
    );
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <PageHeader
          crumbs={[{ label: "首页", to: "/" }, { label: "趣味文章" }]}
          kicker="Reading Corner"
          title="趣味文章"
          description="用轻松的方式聊挠痒这件事：为什么会痒、怎么玩得舒服、道具怎么挑。每篇都能在通勤路上读完。"
          aside={
            <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                <i className="ri-book-open-line text-base"></i>
              </span>
              <div>
                <p className="font-heading text-lg leading-none text-foreground-950">{articles.length}</p>
                <p className="mt-1 text-[11px] text-foreground-500">篇演示文章</p>
              </div>
            </div>
          }
        />

        <div className="mx-auto w-full max-w-[1240px] px-4 py-8 md:px-6 md:py-10">
          <ArticleFilterPanel
            topics={topics}
            topic={topic}
            onTopicChange={(value) => {
              setTopic(value);
              setPage(1);
            }}
            sort={sort}
            onSortChange={setSort}
            tags={allTags}
            activeTags={activeTags}
            onTagToggle={handleTagToggle}
            onReset={reset}
            open={filterOpen}
            onToggle={() => setFilterOpen((prev) => !prev)}
            resultCount={filtered.length}
          />

          <div className="mt-6">
            {visible.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {visible.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <EmptyState
                icon="ri-article-line"
                title="抱歉，未找到符合条件的文章"
                description="换个主题或者少选几个标签试试看，也许会遇到更合心意的一篇。"
                actionLabel="清空筛选条件"
                onAction={reset}
              />
            )}
          </div>

          <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}